import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, CheckCircle, ClockCounterClockwise, UsersThree, Stack } from "@phosphor-icons/react";

export default function TimesheetApprovals() {
  const { user } = useAuth();
  // Approvals is team-scoped for everyone except the dedicated timesheet_admin role.
  // The ITSM 'admin' role is ALWAYS team-scoped here, even if granted timesheet_role 'admin'.
  const seesAll = user?.role === "timesheet_admin";
  const [entries, setEntries] = useState([]);
  const [lateRequests, setLateRequests] = useState([]);
  const [lateStats, setLateStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null); // entry
  const [comment, setComment] = useState("");
  const [showKra, setShowKra] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, lr, ls] = await Promise.all([
        api.get("/timesheet/pending"),
        api.get("/timesheet/late-requests/pending"),
        api.get("/timesheet/late-stats"),
      ]);
      setEntries(p.data);
      setLateRequests(lr.data);
      setLateStats(ls.data.users || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decideLate = async (id, decision) => {
    try {
      await api.post(`/timesheet/late-requests/${id}/decision`, { decision });
      toast.success(decision === "approve" ? "Late submission approved" : "Late request rejected");
      setLateRequests((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const decideDay = async (userId, date, decision, cmt = "") => {
    try {
      await api.post("/timesheet/decide-day", { user_id: userId, date, decision, comment: cmt });
      toast.success(decision === "approve" ? "Day approved" : "Day rejected");
      setEntries((prev) => prev.filter((e) => !(e.user_id === userId && e.date === date)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const decideAllForUser = async (userId, userName) => {
    if (!window.confirm(`Approve ALL submitted timesheets for ${userName}?`)) return;
    try {
      const { data } = await api.post("/timesheet/decide-user", { user_id: userId, decision: "approve" });
      toast.success(`Approved ${data.updated} entr${data.updated === 1 ? "y" : "ies"} for ${userName}`);
      setEntries((prev) => prev.filter((e) => e.user_id !== userId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk approve failed");
    }
  };

  const confirmReject = async () => {
    if (!rejecting) return;
    await decideDay(rejecting.user_id, rejecting.date, "reject", comment);
    setRejecting(null); setComment("");
  };

  // group by user -> date
  const groups = {};
  entries.forEach((e) => {
    const uk = e.user_name || e.user_email || e.user_id;
    groups[uk] = groups[uk] || {};
    groups[uk][e.date] = groups[uk][e.date] || [];
    groups[uk][e.date].push(e);
  });

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-5xl" data-testid="timesheet-approvals-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Timesheet Approvals</h1>
          <p className="text-sm text-muted-foreground mt-1">Review and approve submitted timesheets from your team.</p>
          <span className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-mono uppercase tracking-wide px-2 py-1 border rounded-sm border-border text-muted-foreground" data-testid="ts-approvals-scope-hint">
            <UsersThree size={13} className="text-primary" />
            {seesAll ? "Showing all teams" : "Showing your team only"}
          </span>
        </div>
        <label className="flex items-center gap-2 cursor-pointer shrink-0 mt-1">
          <Switch checked={showKra} onCheckedChange={setShowKra} data-testid="ts-approvals-kra-toggle" />
          <span className="text-xs font-mono uppercase tracking-wide text-muted-foreground">Show KRA</span>
        </label>
      </div>

      {/* Monthly late-submission counts per team member */}
      {lateStats.length > 0 && (
        <Card className="p-4" data-testid="ts-late-stats">
          <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-3">Late submissions this month</div>
          <div className="flex flex-wrap gap-2">
            {lateStats.map((u) => (
              <div key={u.user_id} className="px-3 py-2 border border-border rounded-sm text-sm flex items-center gap-2" data-testid={`ts-late-stat-${u.user_id}`}>
                <span>{u.user_name}</span>
                <Badge variant="outline" className="bg-rag-red-bg text-rag-red-fg border-rag-red/30">{u.late_count} late</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Pending late-submission requests */}
      {lateRequests.length > 0 && (
        <div className="space-y-3" data-testid="ts-late-requests-section">
          <div className="font-display text-lg flex items-center gap-2">
            <ClockCounterClockwise size={18} /> Late submission requests
          </div>
          {lateRequests.map((r) => (
            <Card key={r.id} className="p-4 flex items-start justify-between gap-3" data-testid={`ts-late-request-${r.id}`}>
              <div className="min-w-0">
                <div className="text-sm"><span className="font-medium">{r.user_name}</span> · <span className="font-mono">{r.date}</span></div>
                {r.reason && <div className="text-sm text-muted-foreground mt-1">Reason: {r.reason}</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" onClick={() => decideLate(r.id, "approve")} data-testid={`ts-late-approve-${r.id}`}>
                  <Check size={14} className="mr-1" /> Allow late
                </Button>
                <Button size="sm" variant="outline" className="text-rag-red-fg" onClick={() => decideLate(r.id, "reject")} data-testid={`ts-late-reject-${r.id}`}>
                  <X size={14} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {loading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card>
      ) : entries.length === 0 ? (
        <Card className="p-10 text-center" data-testid="ts-approvals-empty">
          <CheckCircle size={32} className="mx-auto text-rag-green-fg mb-2" />
          <div className="font-display text-lg">All caught up</div>
          <div className="text-sm text-muted-foreground">No timesheets awaiting your approval.</div>
        </Card>
      ) : (
        Object.entries(groups).map(([userName, byDate]) => {
          const guid = Object.values(byDate)[0][0].user_id;
          const dayCount = Object.keys(byDate).length;
          return (
          <div key={userName} className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-display text-lg">{userName} <span className="text-sm font-normal text-muted-foreground font-mono">· {dayCount} day{dayCount > 1 ? "s" : ""} pending</span></div>
              <Button size="sm" variant="outline" className="rounded-sm font-mono text-[11px] uppercase border-rag-green-fg/40 text-rag-green-fg hover:bg-rag-green-fg/10" onClick={() => decideAllForUser(guid, userName)} data-testid={`ts-approve-all-${guid}`}>
                <Stack size={14} className="mr-1.5" /> Approve all
              </Button>
            </div>
            {Object.entries(byDate).map(([date, list]) => {
              const total = Math.round(list.reduce((s, e) => s + (e.total_hours || 0), 0) * 100) / 100;
              const uid = list[0].user_id;
              const hasLate = list.some((e) => e.late);
              return (
                <Card key={date} className="p-4" data-testid={`ts-approval-day-${date}`}>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="font-mono text-sm flex items-center gap-2">
                      {date} · <span className="text-muted-foreground">{list.length} entr{list.length === 1 ? "y" : "ies"} · {total}h total</span>
                      {hasLate && <Badge variant="outline" className="bg-rag-red-bg text-rag-red-fg border-rag-red/30 text-[10px]">late</Badge>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => decideDay(uid, date, "approve")} data-testid={`ts-approve-day-${date}`}>
                        <Check size={14} className="mr-1.5" /> Approve day
                      </Button>
                      <Button size="sm" variant="outline" className="text-rag-red-fg" onClick={() => setRejecting({ user_id: uid, date, user_name: userName })} data-testid={`ts-reject-day-${date}`}>
                        <X size={14} className="mr-1.5" /> Reject day
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {list.map((e) => (
                      <div key={e.id} className="flex items-start justify-between gap-3 border-t border-border pt-2" data-testid={`ts-approval-entry-${e.id}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{e.task_name}</span>
                            {showKra && e.kra_name && <span className="text-xs text-muted-foreground" data-testid={`ts-approval-kra-${e.id}`}>· {e.kra_name}</span>}
                            {e.client_name && <Badge variant="outline" className="text-[10px]">{e.client_name}</Badge>}
                            {e.late && <Badge variant="outline" className="bg-rag-red-bg text-rag-red-fg border-rag-red/30 text-[10px]">late</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground font-mono mt-0.5">{e.start_time}–{e.end_time} · {e.total_hours}h</div>
                          {e.description && <div className="text-sm text-muted-foreground mt-1">{e.description}</div>}
                        </div>
                        <div className="font-mono text-sm text-muted-foreground shrink-0">{e.total_hours}h</div>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
          );
        })
      )}

      <Dialog open={!!rejecting} onOpenChange={(o) => { if (!o) { setRejecting(null); setComment(""); } }}>
        <DialogContent data-testid="ts-reject-dialog">
          <DialogHeader><DialogTitle>Reject timesheet</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground">Reject {rejecting?.user_name}'s timesheet for {rejecting?.date}. Let them know why so they can correct and resubmit the day.</DialogDescription>
          <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Reason for rejection" data-testid="ts-reject-comment" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejecting(null); setComment(""); }}>Cancel</Button>
            <Button variant="destructive" onClick={confirmReject} data-testid="ts-confirm-reject">Reject day</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
